import { createRootRoute, createRoute, createRouter, parseSearchWith, redirect } from '@tanstack/react-router';
import { Applicazione } from './Applicazione.js';
import type { TipoAnagrafica } from './api/anagrafiche.js';
import { useSessione } from './api/sessione.js';
import { Anagrafica } from './pagine/Anagrafica.js';
import { Anagrafiche } from './pagine/Anagrafiche.js';
import { Articolo } from './pagine/Articolo.js';
import { Articoli } from './pagine/Articoli.js';
import { DatiGenerici } from './pagine/DatiGenerici.js';
import { Avvisi } from './pagine/Avvisi.js';
import { Documenti } from './pagine/Documenti.js';
import { Integrazioni } from './pagine/Integrazioni.js';
import { Documento } from './pagine/Documento.js';
import { Listini } from './pagine/Listini.js';
import { Trattative } from './pagine/Trattative.js';
import { Trattativa } from './pagine/Trattativa.js';
import { Listino } from './pagine/Listino.js';
import { Home } from './pagine/Home.js';
import { ImportOrdiniExcel } from './pagine/ImportOrdiniExcel.js';
import { InArrivo } from './pagine/InArrivo.js';
import { NonPermessa } from './pagine/NonPermessa.js';
import { NonTrovata } from './pagine/NonTrovata.js';
import { Ordine } from './pagine/Ordine.js';
import { Ordini } from './pagine/Ordini.js';
import { Parametri } from './pagine/Parametri.js';
import { Produzione } from './pagine/Produzione.js';
import { Profilo } from './pagine/Profilo.js';
import { validaRicerca } from './ricerca-url.js';
import { t } from './i18n/lingua.js';
import { type Sezione, usePuoAprire } from './permessi.js';

/**
 * Percorsi.
 *
 * Le sei voci delle anagrafiche sono sei indirizzi diversi ma una pagina
 * sola: cambia il tipo, e con quello il titolo. Le sezioni non ancora
 * costruite passano tutte da `/coming-soon`.
 */

const radice = createRootRoute({
  component: Applicazione,
  // Senza questa, un indirizzo sconosciuto lascia lo schermo bianco e chi lo
  // apre non sa se è rotto il programma o sbagliato il collegamento.
  notFoundComponent: NonTrovata,
});

function PaginaHome() {
  const { data: sessione } = useSessione();
  return sessione ? <Home sessione={sessione} /> : null;
}

const home = createRoute({ getParentRoute: () => radice, path: '/', component: PaginaHome });

/**
 * Una pagina che appartiene a una sezione: chi non la apre vede che cosa
 * succede, invece di una pagina che si riempie di errori. Il controllo vero
 * sta sul server; questo evita solo di mostrare una porta che non si apre.
 */
function Protetta({ sezione, children }: { sezione: Sezione; children: React.ReactNode }) {
  const puo = usePuoAprire();
  return puo(sezione) ? <>{children}</> : <NonPermessa />;
}

function protetta(sezione: Sezione, Pagina: () => React.ReactNode) {
  return function PaginaProtetta() {
    return (
      <Protetta sezione={sezione}>
        <Pagina />
      </Protetta>
    );
  };
}

export const ANAGRAFICHE: { percorso: string; tipo: TipoAnagrafica; titolo: string }[] = [
  { percorso: '/customers', tipo: 'customer', titolo: t('Clienti') },
  { percorso: '/suppliers', tipo: 'supplier', titolo: t('Fornitori') },
  { percorso: '/agents', tipo: 'agent', titolo: t('Agenti') },
  { percorso: '/leads', tipo: 'lead', titolo: t('Lead') },
  { percorso: '/prospects', tipo: 'prospect', titolo: t('Prospect') },
  { percorso: '/operators', tipo: 'operator', titolo: t('Operatori') },
];

const rotteAnagrafiche = ANAGRAFICHE.map((voce) =>
  createRoute({
    getParentRoute: () => radice,
    path: voce.percorso,
    // la ricerca sta nell'indirizzo: un elenco filtrato si manda con un link
    validateSearch: validaRicerca,
    component: () => (
      <Protetta sezione={voce.percorso.slice(1) as Sezione}>
        <Anagrafiche tipo={voce.tipo} titolo={voce.titolo} percorso={voce.percorso} />
      </Protetta>
    ),
  }),
);

/** La scheda: `/customers/1270`, e così per ogni tipo di anagrafica. */
const rotteScheda = ANAGRAFICHE.map((voce) => {
  const rotta = createRoute({
    getParentRoute: () => radice,
    path: `${voce.percorso}/$id`,
    component: function PaginaScheda() {
      const { id } = rotta.useParams();
      return (
        <Protetta sezione={voce.percorso.slice(1) as Sezione}>
          <Anagrafica id={Number(id)} elenco={voce.percorso} />
        </Protetta>
      );
    },
  });
  return rotta;
});

const listini = createRoute({
  getParentRoute: () => radice,
  path: '/price-lists',
  validateSearch: validaRicerca,
  component: protetta('price-lists', Listini),
});

const listino = createRoute({
  getParentRoute: () => radice,
  path: '/price-lists/$id',
  component: function PaginaListino() {
    const { id } = listino.useParams();
    return (
      <Protetta sezione="price-lists">
        <Listino id={Number(id)} />
      </Protetta>
    );
  },
});

const documenti = createRoute({
  getParentRoute: () => radice,
  path: '/documents',
  validateSearch: validaRicerca,
  component: protetta('documents', Documenti),
});

const documento = createRoute({
  getParentRoute: () => radice,
  path: '/documents/$id',
  component: function PaginaDocumento() {
    const { id } = documento.useParams();
    return (
      <Protetta sezione="documents">
        <Documento id={Number(id)} />
      </Protetta>
    );
  },
});

const trattative = createRoute({
  getParentRoute: () => radice,
  path: '/negotiations',
  validateSearch: validaRicerca,
  component: protetta('negotiations', Trattative),
});

const trattativa = createRoute({
  getParentRoute: () => radice,
  path: '/negotiations/$id',
  component: function PaginaTrattativa() {
    const { id } = trattativa.useParams();
    return (
      <Protetta sezione="negotiations">
        <Trattativa id={Number(id)} />
      </Protetta>
    );
  },
});

const avvisi = createRoute({
  getParentRoute: () => radice,
  path: '/alerts',
  // gli avvisi ETA sono degli ordini
  component: protetta('sales-orders', Avvisi),
});

const integrazioni = createRoute({
  getParentRoute: () => radice,
  path: '/integrations',
  component: protetta('integrations', Integrazioni),
});

const datiGenerici = createRoute({
  getParentRoute: () => radice,
  path: '/reference-data',
  component: protetta('reference-data', DatiGenerici),
});

const articoli = createRoute({
  getParentRoute: () => radice,
  path: '/items',
  validateSearch: validaRicerca,
  component: protetta('items', Articoli),
});

const articolo = createRoute({
  getParentRoute: () => radice,
  path: '/items/$id',
  component: function PaginaArticolo() {
    const { id } = articolo.useParams();
    return (
      <Protetta sezione="items">
        <Articolo id={Number(id)} />
      </Protetta>
    );
  },
});

const ordini = createRoute({
  getParentRoute: () => radice,
  path: '/sales-orders',
  validateSearch: validaRicerca,
  component: protetta('sales-orders', Ordini),
});

/**
 * L'importazione da Excel.
 *
 * Sta prima di `/sales-orders/$id` perché un pezzo di percorso scritto vince
 * su un parametro: `import` non è mai il numero di un ordine.
 */
const importOrdini = createRoute({
  getParentRoute: () => radice,
  path: '/sales-orders/import',
  component: protetta('sales-orders', ImportOrdiniExcel),
});

const ordine = createRoute({
  getParentRoute: () => radice,
  path: '/sales-orders/$id',
  component: function PaginaOrdine() {
    const { id } = ordine.useParams();
    return (
      <Protetta sezione="sales-orders">
        <Ordine id={Number(id)} />
      </Protetta>
    );
  },
});

/** «Da confermare»: gli ordini da portare allo stato 2, senza prezzi. */
const produzione = createRoute({
  getParentRoute: () => radice,
  path: '/production',
  // la ricerca e i filtri stanno nell'indirizzo, come negli Ordini
  validateSearch: validaRicerca,
  component: protetta('production', Produzione),
});

/**
 * I vecchi indirizzi in italiano portano ai nuovi, con i filtri che avevano.
 *
 * Non sono mai stati in produzione, ma sono nei preferiti e nella cronologia
 * di chi ha provato la 3.0 in questi giorni: farli morire in silenzio è il
 * modo peggiore di cambiare un nome.
 */
const VECCHI: Record<string, string> = {
  '/clienti': '/customers',
  '/fornitori': '/suppliers',
  '/agenti': '/agents',
  '/lead': '/leads',
  '/prospect': '/prospects',
  '/operatori': '/operators',
  '/articoli': '/items',
  '/listini': '/price-lists',
  '/trattative': '/negotiations',
  '/ordini': '/sales-orders',
  '/documenti': '/documents',
  '/dati-generici': '/reference-data',
  '/in-arrivo': '/coming-soon',
};

/** Anche i nomi dei filtri sono cambiati: un vecchio link resta valido. */
const VECCHI_FILTRI: Record<string, string> = {
  paeseId: 'countryId',
  citta: 'city',
  provincia: 'province',
  indirizzo: 'address',
  telefono: 'phone',
  categoriaId: 'categoryId',
  sezionaleId: 'sectionalId',
  valuta: 'currency',
  prepagamento: 'prepayment',
  invioVettori: 'sendToCarriers',
  approvvigionamentoAutomatico: 'automaticSupply',
  codiceBc: 'bcCode',
  pagina: 'page',
  dimensione: 'size',
  ordina: 'sort',
  verso: 'dir',
  cerca: 'q',
  archiviati: 'archived',
};

const rotteVecchie = Object.entries(VECCHI).map(([vecchio, nuovo]) =>
  createRoute({
    getParentRoute: () => radice,
    path: vecchio,
    validateSearch: validaRicerca,
    beforeLoad: ({ search }) => {
      const tradotta: Record<string, unknown> = {};
      for (const [chiave, valore] of Object.entries(search)) {
        tradotta[VECCHI_FILTRI[chiave] ?? chiave] = valore;
      }
      throw redirect({ to: nuovo, search: tradotta as never, replace: true });
    },
    component: () => null,
  }),
);

const parametri = createRoute({
  getParentRoute: () => radice,
  path: '/settings',
  component: protetta('settings', Parametri),
});

const profilo = createRoute({
  getParentRoute: () => radice,
  path: '/profile',
  component: function PaginaProfilo() {
    const { data: sessione } = useSessione();
    return sessione ? <Profilo sessione={sessione} /> : null;
  },
});

const inArrivo = createRoute({
  getParentRoute: () => radice,
  path: '/coming-soon',
  validateSearch: (ricerca: Record<string, unknown>) => ({
    sezione: typeof ricerca.sezione === 'string' ? ricerca.sezione : t('Questa sezione'),
  }),
  component: function PaginaInArrivo() {
    const { sezione } = inArrivo.useSearch();
    return <InArrivo sezione={sezione} />;
  },
});

/**
 * Come si legge l'indirizzo.
 *
 * Il router lo legge come JSON: `?q=4886898700` diventava il numero
 * 4886898700, e la ricerca — che è testo — lo scartava. Un link a un PO
 * scritto a mano o incollato da una mail apriva l'elenco intero (Antonio, 26
 * ottobre 2026). E un numero lungo perdeva le ultime cifre: un PO di venti
 * cifre arrivava con «…567000» in coda.
 *
 * Qui un valore resta numero solo se riscritto torna identico a com'era
 * nell'indirizzo; altrimenti resta il testo scritto. La ricerca libera è
 * sempre testo: un PO di sole cifre è un nome, non un numero.
 */
const leggiComeJson = parseSearchWith(JSON.parse);

export function leggiIndirizzo(cerca: string): Record<string, unknown> {
  const valori: Record<string, unknown> = { ...leggiComeJson(cerca) };
  const scritti = new URLSearchParams(cerca);
  for (const [chiave, valore] of Object.entries(valori)) {
    const scritto = scritti.get(chiave);
    if (scritto === null || typeof valore === 'string' || typeof valore === 'object') continue;
    if (chiave === 'q' || String(valore) !== scritto) valori[chiave] = scritto;
  }
  return valori;
}

export const router = createRouter({
  parseSearch: leggiIndirizzo,
  routeTree: radice.addChildren([
    home,
    ...rotteAnagrafiche,
    ...rotteScheda,
    articoli,
    articolo,
    datiGenerici,
    documenti,
    documento,
    trattative,
    trattativa,
    avvisi,
    integrazioni,
    listini,
    listino,
    ordini,
    importOrdini,
    ordine,
    produzione,
    parametri,
    profilo,
    ...rotteVecchie,
    inArrivo,
  ]),
  defaultPreload: 'intent',
  defaultNotFoundComponent: NonTrovata,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
